import isEmpty from 'lodash/isEmpty'

/**
 * A wrapper for `window.sessionStorage` that provides safe access to session storage
 * in both browser and non-browser environments. If executed in a non-browser environment
 * (e.g., during server-side rendering), it provides a no-op implementation to avoid errors.
 *
 * @constant
 * @type {Storage}
 * @property {function(string): (string | undefined)} getItem - Retrieves the value associated with the given key. Returns `undefined` in non-browser environments.
 * @property {function(string, string): void} setItem - Stores a key-value pair in session storage. No-op in non-browser environments.
 * @property {function(string): void} removeItem - Removes the key-value pair associated with the given key. No-op in non-browser environments.
 */
const sessionStorage =
  typeof window === 'undefined'
    ? {
        getItem: () => undefined,
        setItem: () => undefined,
        removeItem: () => undefined
      }
    : window.sessionStorage

/**
 * An object containing keys used for session persistence in the application.
 * Each key represents a specific piece of data stored in the session.
 *
 * @constant {Object} keyList
 * @property {string} RANGE_DATE - Key for storing the selected date range.
 * @property {string} RANGE_DATE_PAYOUT - Key for storing the payout date range.
 * @property {string} SELLER - Key for storing the selected seller.
 * @property {string} STATEPRODUCT - Key for storing the selected product state.
 * @property {string} BRAND - Key for storing the selected brand.
 * @property {string} CATEGORY - Key for storing the selected category.
 */
const keyList = {
  RANGE_DATE: '@range-date',
  RANGE_DATE_PAYOUT: '@range-date-payout',
  SELLER: '@seller-select',
  STATEPRODUCT: '@state-product-select',
  BRAND: '@brand-select',
  CATEGORY: '@category-select'
}

/**
 * Retrieves an item from the session storage by its key.
 * If the stored value is the string 'undefined', it returns `undefined`.
 * If the stored value is the string 'null', it returns `null`.
 * Attempts to parse the stored value as JSON. If parsing fails, returns the raw string value.
 *
 * @param {string} key - The key of the item to retrieve from session storage.
 * @returns {any} The parsed value from session storage, or the raw string if parsing fails.
 */
const getItem = key => {
  const item = sessionStorage.getItem(key)
  if (item === 'undefined') {
    return undefined
  }
  if (item === 'null') {
    return null
  }
  try {
    return JSON.parse(item, 'base64')
  } catch (cause) {
    return item
  }
}

/**
 * Stores a value in the session storage under the specified key.
 *
 * @param {string} key - The key under which the value will be stored.
 * @param {*} value - The value to be stored. It will be stringified to JSON format.
 */
const setItem = (key, value) =>
  sessionStorage.setItem(key, JSON.stringify(value))

/**
 * Removes an item from the session storage.
 *
 * @param {string} key - The key of the item to remove from session storage.
 */
const removeItem = key => sessionStorage.removeItem(key)

/**
 * A utility object for managing session-persistent storage operations.
 * Provides methods to get, set, remove, and check the existence of various session-persistent data items.
 *
 * @returns {Object} An object containing the following methods:
 *
 * @method getRangeDate
 *   Retrieves the stored range date.
 *   @returns {*} The stored range date value.
 *
 * @method setRangeDate
 *   Stores a range date value.
 *   @param {*} date - The range date value to store.
 *
 * @method removeRangeDate
 *   Removes the stored range date.
 *
 * @method isEmptyRangeDate
 *   Checks if the range date is empty.
 *   @returns {boolean} True if the range date is empty, false otherwise.
 *
 * @method getRangeDatePayout
 *   Retrieves the stored range date for payouts.
 *   @returns {*} The stored range date payout value.
 *
 * @method setRangeDatePayout
 *   Stores a range date payout value.
 *   @param {*} date - The range date payout value to store.
 *
 * @method removeRangeDatePayout
 *   Removes the stored range date payout.
 *
 * @method isEmptyRangeDatePayout
 *   Checks if the range date payout is empty.
 *   @returns {boolean} True if the range date payout is empty, false otherwise.
 *
 * @method getSellerSelect
 *   Retrieves the stored seller selection.
 *   @returns {*} The stored seller selection value.
 *
 * @method setSellerSelect
 *   Stores a seller selection value.
 *   @param {*} seller - The seller selection value to store.
 *
 * @method removeSellerSelect
 *   Removes the stored seller selection.
 *
 * @method isEmptySellerSelect
 *   Checks if the seller selection is empty.
 *   @returns {boolean} True if the seller selection is empty, false otherwise.
 *
 * @method getBrandSelect
 *   Retrieves the stored brand selection.
 *   @returns {*} The stored brand selection value.
 *
 * @method setBrandSelect
 *   Stores a brand selection value.
 *   @param {*} brand - The brand selection value to store.
 *
 * @method removeBrandSelect
 *   Removes the stored brand selection.
 *
 * @method isEmptyBrandSelect
 *   Checks if the brand selection is empty.
 *   @returns {boolean} True if the brand selection is empty, false otherwise.
 *
 * @method getCategorySelect
 *   Retrieves the stored category selection.
 *   @returns {*} The stored category selection value.
 *
 * @method setCategorySelect
 *   Stores a category selection value.
 *   @param {*} category - The category selection value to store.
 *
 * @method removeCategorySelect
 *   Removes the stored category selection.
 *
 * @method isEmptyCategorySelect
 *   Checks if the category selection is empty.
 *   @returns {boolean} True if the category selection is empty, false otherwise.
 *
 * @method getStateFilter
 *   Retrieves the stored state filter.
 *   @returns {*} The stored state filter value.
 *
 * @method setStateFilter
 *   Stores a state filter value.
 *   @param {*} state - The state filter value to store.
 *
 * @method removeStateFilter
 *   Removes the stored state filter.
 *
 * @method isEmptyStateFilter
 *   Checks if the state filter is empty.
 *   @returns {boolean} True if the state filter is empty, false otherwise.
 */
const sessionPersistentStore = () => ({
  getRangeDate: () => getItem(keyList.RANGE_DATE),
  setRangeDate: date => setItem(keyList.RANGE_DATE, date),
  removeRangeDate: () => removeItem(keyList.RANGE_DATE),
  isEmptyRangeDate: () => isEmpty(sessionPersistentStore().getRangeDate()),
  getRangeDatePayout: () => getItem(keyList.RANGE_DATE_PAYOUT),
  setRangeDatePayout: date => setItem(keyList.RANGE_DATE_PAYOUT, date),
  removeRangeDatePayout: () => removeItem(keyList.RANGE_DATE_PAYOUT),
  isEmptyRangeDatePayout: () =>
    isEmpty(sessionPersistentStore().getRangeDatePayout()),
  getSellerSelect: () => getItem(keyList.SELLER),
  setSellerSelect: seller => setItem(keyList.SELLER, seller),
  removeSellerSelect: () => removeItem(keyList.SELLER),
  isEmptySellerSelect: () =>
    isEmpty(sessionPersistentStore().getSellerSelect()),
  getBrandSelect: () => getItem(keyList.BRAND),
  setBrandSelect: brand => setItem(keyList.BRAND, brand),
  removeBrandSelect: () => removeItem(keyList.BRAND),
  isEmptyBrandSelect: () =>
    isEmpty(sessionPersistentStore().getBrandSelect()),
  getCategorySelect: () => getItem(keyList.CATEGORY),
  setCategorySelect: category => setItem(keyList.CATEGORY, category),
  removeCategorySelect: () => removeItem(keyList.CATEGORY),
  isEmptyCategorySelect: () =>
    isEmpty(sessionPersistentStore().getCategorySelect()),
  getStateFilter: () => getItem(keyList.STATEPRODUCT),
  setStateFilter: state => setItem(keyList.STATEPRODUCT, state),
  removeStateFilter: () => removeItem(keyList.STATEPRODUCT),
  isEmptyStateFilter: () =>
    isEmpty(sessionPersistentStore().getStateFilter())
})

export default sessionPersistentStore()
